import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

function StatusPill({ s }) {
  const map = {
    submitted: "bg-primary/10 text-primary",
    approved: "bg-emerald-500/10 text-emerald-600",
    disbursed: "bg-sky-500/10 text-sky-600",
    rejected: "bg-red-500/10 text-red-600",
    closed: "bg-muted text-muted-foreground",
  };
  return <span className={`px-2 py-1 rounded-full text-xs ${map[s] || "bg-muted"}`}>{s}</span>;
}

export default function LoanStatus() {
  const [loans, setLoans] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => { api.get("/loans").then((r) => setLoans(r.data)).catch(() => {}); }, []);

  const openLoan = async (id) => {
    const r = await api.get(`/loans/${id}`);
    setSelected(r.data);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary font-semibold">Loans</div>
            <h1 className="font-display text-3xl mt-2">Loan status & timeline</h1>
          </div>
          <Link to="/app/apply"><Button className="rounded-full brand-gradient text-white">Apply new</Button></Link>
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Tenure</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="loans-table">
              {loans.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No applications yet.</TableCell></TableRow>}
              {loans.map((l) => (
                <TableRow key={l.id} className="cursor-pointer hover:bg-muted/40" onClick={() => openLoan(l.id)}>
                  <TableCell className="font-mono text-xs">{l.id.slice(0, 8)}</TableCell>
                  <TableCell className="capitalize">{l.loan_type.replace("_"," ")}</TableCell>
                  <TableCell>₹{Number(l.amount).toLocaleString("en-IN")}</TableCell>
                  <TableCell>{l.tenure_months} mo</TableCell>
                  <TableCell><StatusPill s={l.status} /></TableCell>
                  <TableCell>{new Date(l.created_at).toLocaleDateString()}</TableCell>
                  <TableCell><Button size="sm" variant="outline">View</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        {selected && (
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-primary font-semibold">Application</div>
                <div className="font-display text-xl mt-1">{selected.id.slice(0, 8)} · {selected.loan_type}</div>
              </div>
              <StatusPill s={selected.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-6">
              <Metric k="Amount" v={`₹${Number(selected.amount).toLocaleString("en-IN")}`} />
              <Metric k="Approved" v={selected.approved_amount ? `₹${Number(selected.approved_amount).toLocaleString("en-IN")}` : "—"} />
              <Metric k="Rate" v={`${selected.interest_rate || "—"}%`} />
              <Metric k="Tenure" v={`${selected.tenure_months} mo`} />
              <Metric k="Created" v={new Date(selected.created_at).toLocaleDateString()} />
            </div>
            <div>
              <div className="font-medium mb-3">Timeline</div>
              <ol className="relative border-l border-border ml-2">
                {(selected.timeline || []).map((t, i) => (
                  <li key={i} className="ml-4 pb-4">
                    <div className="absolute -left-1.5 h-3 w-3 rounded-full brand-gradient" />
                    <div className="text-sm capitalize">{t.stage}</div>
                    <div className="text-xs text-muted-foreground">{new Date(t.at).toLocaleString()}</div>
                    {t.note && <div className="text-xs mt-1">{t.note}</div>}
                  </li>
                ))}
              </ol>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
function Metric({ k, v }) { return <div className="rounded-xl bg-muted p-3"><div className="text-xs text-muted-foreground">{k}</div><div className="font-medium">{v}</div></div>; }
